"use client";

import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import React from "react";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded border border-transparent px-1.5 py-0.5 text-xs font-semibold transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        // shadcn stock variants
        default: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        destructive: "bg-destructive text-destructive-foreground",
        outline: "border-border text-foreground",
        // Gondola domain variants (badge tokens)
        neutral: "bg-badge-neutral text-badge-neutral-foreground",
        positive: "bg-badge-positive text-badge-positive-foreground",
        negative:
          "border-badge-negative-border/40 bg-transparent text-badge-negative-foreground",
        // Loyalty program badge — dynamic bg/text supplied by caller via style + className
        loyalty: "border-border",
        // Gondola brand highlight (Top Pick, Gondola Perks, Direct Booking, etc.)
        "brand-gold": "border-brand-gold/50 bg-brand-gold/20 text-brand-gold",
        // Subtle muted (spec labels, neutral status)
        muted: "border-transparent bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Accessible label for screen readers (e.g. "Reservation cancelled") */
    ariaLabel?: string;
  };

const Badge: React.FC<BadgeProps> = ({
  children,
  variant,
  className,
  ariaLabel,
  ...props
}) => {
  // Only badges wired as buttons get press feedback — a static status label
  // shouldn't scale on tap. Adds transform to the transition and the signature
  // ease, with a reduced-motion guard.
  const isInteractive = props.onClick != null;
  return (
    <span
      className={cn(
        badgeVariants({ variant }),
        isInteractive &&
          "cursor-pointer transition-[color,box-shadow,transform] ease-signature active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100",
        className
      )}
      role={ariaLabel ? "status" : undefined}
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </span>
  );
};

export { Badge, badgeVariants };
export type { BadgeProps };
export default Badge;
